import { useMemo } from 'react'
import { cn } from '../../shared/utils/cn'
import { ExportSensorConfig, ExportSensorSelection as SensorSelection } from '../../utils/csvExportTypes'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle, faChevronDown } from '@fortawesome/free-solid-svg-icons'

interface ExportSensorSelectionProps {
  sensors: ExportSensorConfig[]
  selectedSensors: Map<string, SensorSelection>
  onSelectionChange: (port: string, selected: boolean) => void
  onUnitChange: (port: string, unitId: number, unitSymbol: string) => void
  onSelectAll: (selected: boolean) => void
  availableUnits: Record<number, Record<number, string>>  // unitCategoryId -> { unitId -> symbol }
}

interface SensorGroup {
  parentPort: string
  productName: string
  sensors: ExportSensorConfig[]
  isGrouped: boolean
}

function UnitSelector({
  sensor,
  isSelected,
  isDisabled,
  currentUnitId,
  currentUnitSymbol,
  units,
  onUnitChange,
}: {
  sensor: ExportSensorConfig
  isSelected: boolean
  isDisabled: boolean
  currentUnitId: number
  currentUnitSymbol: string
  units: Record<number, string>
  onUnitChange: (port: string, unitId: number, unitSymbol: string) => void
}) {
  const hasMultipleUnits = Object.keys(units).length > 1

  if (hasMultipleUnits) {
    return (
      <div className="relative">
        <select
          value={currentUnitId}
          onChange={(e) => {
            const newUnitId = Number(e.target.value)
            const newSymbol = units[newUnitId] || ''
            onUnitChange(sensor.port, newUnitId, newSymbol)
          }}
          disabled={!isSelected || isDisabled}
          className={cn(
            'appearance-none pl-2 pr-6 py-1 text-xs rounded border',
            isSelected && !isDisabled
              ? 'bg-white border-gray-300 text-gray-900'
              : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
          )}
        >
          {Object.entries(units).map(([unitId, symbol]) => (
            <option key={unitId} value={unitId}>
              {symbol}
            </option>
          ))}
        </select>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={cn(
            'absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 pointer-events-none',
            isSelected && !isDisabled ? 'text-gray-500' : 'text-gray-300'
          )}
        />
      </div>
    )
  }

  return (
    <span className={cn(
      'text-xs px-2 py-1 rounded',
      isSelected && !isDisabled ? 'text-gray-700 bg-gray-100' : 'text-gray-400'
    )}>
      {currentUnitSymbol}
    </span>
  )
}

export function ExportSensorSelection({
  sensors,
  selectedSensors,
  onSelectionChange,
  onUnitChange,
  onSelectAll,
  availableUnits
}: ExportSensorSelectionProps) {
  // Count selected sensors and sensors with data
  const { selectedCount, sensorsWithData } = useMemo(() => {
    let count = 0
    let withData = 0
    for (const sensor of sensors) {
      if (sensor.hasData) withData++
      const selection = selectedSensors.get(sensor.port)
      if (selection?.selected) count++
    }
    return { selectedCount: count, sensorsWithData: withData }
  }, [selectedSensors, sensors])

  // Group sensors: sub-row sensors grouped under their parent port
  const sensorGroups = useMemo((): SensorGroup[] => {
    const groups: SensorGroup[] = []
    const parentMap = new Map<string, ExportSensorConfig[]>()
    const parentProductNames = new Map<string, string>()

    for (const sensor of sensors) {
      if (sensor.isSubRow && sensor.parentPort) {
        const existing = parentMap.get(sensor.parentPort) || []
        existing.push(sensor)
        parentMap.set(sensor.parentPort, existing)
        if (!parentProductNames.has(sensor.parentPort)) {
          parentProductNames.set(sensor.parentPort, sensor.productName)
        }
      } else {
        // Check if this is a parent of sub-rows (don't add standalone if it has sub-rows)
        // We'll handle this after iterating
        groups.push({
          parentPort: sensor.port,
          productName: sensor.productName,
          sensors: [sensor],
          isGrouped: false,
        })
      }
    }

    // Replace standalone entries that have sub-rows
    const result: SensorGroup[] = []
    const processedParents = new Set<string>()

    for (const group of groups) {
      const subRows = parentMap.get(group.parentPort)
      if (subRows && subRows.length > 0) {
        result.push({
          parentPort: group.parentPort,
          productName: group.productName,
          sensors: subRows,
          isGrouped: true,
        })
        processedParents.add(group.parentPort)
      } else {
        result.push(group)
      }
    }

    // Add grouped sensors that don't have a standalone parent entry
    for (const [parentPort, subSensors] of parentMap) {
      if (!processedParents.has(parentPort)) {
        result.push({
          parentPort,
          productName: parentProductNames.get(parentPort) || 'Unknown',
          sensors: subSensors,
          isGrouped: true,
        })
      }
    }

    return result
  }, [sensors])

  // Determine select all state (only considers sensors with data)
  const selectAllState = useMemo((): 'all' | 'none' | 'some' => {
    if (sensorsWithData === 0) return 'none'
    if (selectedCount === sensorsWithData) return 'all'
    if (selectedCount === 0) return 'none'
    return 'some'
  }, [selectedCount, sensorsWithData])

  const handleSelectAllChange = () => {
    const shouldSelectAll = selectAllState !== 'all'
    for (const sensor of sensors) {
      if (sensor.hasData) {
        onSelectionChange(sensor.port, shouldSelectAll)
      }
    }
  }

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        Sensors to Export
      </label>

      {/* Select All */}
      <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={selectAllState === 'all'}
            ref={(el) => {
              if (el) el.indeterminate = selectAllState === 'some'
            }}
            onChange={handleSelectAllChange}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">
            Select All
          </span>
        </label>
        <span className="text-xs text-gray-500">
          ({selectedCount}/{sensors.length} sensors)
        </span>
      </div>

      {/* Sensor List */}
      <div className="overflow-y-auto border border-gray-200 rounded-md">
        {sensorGroups.map((group) => {
          if (group.isGrouped) {
            return (
              <SensorGroupRow
                key={group.parentPort}
                group={group}
                selectedSensors={selectedSensors}
                onSelectionChange={onSelectionChange}
                onUnitChange={onUnitChange}
                availableUnits={availableUnits}
              />
            )
          }

          // Render standard (non-grouped) sensor row
          const sensor = group.sensors[0]
          return (
            <SensorRow
              key={sensor.port}
              sensor={sensor}
              selectedSensors={selectedSensors}
              onSelectionChange={onSelectionChange}
              onUnitChange={onUnitChange}
              availableUnits={availableUnits}
            />
          )
        })}
      </div>
    </div>
  )
}

function SensorRow({
  sensor,
  selectedSensors,
  onSelectionChange,
  onUnitChange,
  availableUnits,
}: {
  sensor: ExportSensorConfig
  selectedSensors: Map<string, SensorSelection>
  onSelectionChange: (port: string, selected: boolean) => void
  onUnitChange: (port: string, unitId: number, unitSymbol: string) => void
  availableUnits: Record<number, Record<number, string>>
}) {
  const selection = selectedSensors.get(sensor.port)
  const isSelected = selection?.selected ?? false
  const currentUnitId = selection?.unitId ?? sensor.unitId
  const currentUnitSymbol = selection?.unitSymbol ?? sensor.unitSymbol
  const isDisabled = !sensor.hasData
  const units = availableUnits[sensor.unitCategoryId] || {}

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 px-3 py-2',
        'border-b border-gray-100 last:border-b-0',
        isDisabled
          ? 'bg-gray-50 opacity-60'
          : isSelected ? 'bg-blue-50/50' : 'bg-white'
      )}
    >
      <label className={cn(
        'flex items-center gap-2 flex-1',
        isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
      )}>
        <input
          type="checkbox"
          checked={isSelected}
          disabled={isDisabled}
          onChange={(e) => onSelectionChange(sensor.port, e.target.checked)}
          className={cn(
            'h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500',
            isDisabled && 'cursor-not-allowed'
          )}
        />
        <span className={cn(
          'inline-flex items-center justify-center',
          'w-7 h-5 text-xs font-bold rounded',
          isDisabled
            ? 'bg-gray-200 text-gray-400'
            : 'bg-gray-100 text-gray-700'
        )}>
          {sensor.port}
        </span>
        <span className={cn(
          'text-sm truncate',
          isDisabled ? 'text-gray-400' : 'text-gray-900'
        )}>
          {sensor.productName}
        </span>
        {!sensor.hasData && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-amber-700 bg-amber-100 rounded">
            <FontAwesomeIcon icon={faExclamationTriangle} className="h-3 w-3" />
            No data
          </span>
        )}
      </label>

      <UnitSelector
        sensor={sensor}
        isSelected={isSelected}
        isDisabled={isDisabled}
        currentUnitId={currentUnitId}
        currentUnitSymbol={currentUnitSymbol}
        units={units}
        onUnitChange={onUnitChange}
      />
    </div>
  )
}

function SensorGroupRow({
  group,
  selectedSensors,
  onSelectionChange,
  onUnitChange,
  availableUnits,
}: {
  group: SensorGroup
  selectedSensors: Map<string, SensorSelection>
  onSelectionChange: (port: string, selected: boolean) => void
  onUnitChange: (port: string, unitId: number, unitSymbol: string) => void
  availableUnits: Record<number, Record<number, string>>
}) {
  const subSensors = group.sensors
  const hasAnyData = subSensors.some(s => s.hasData)

  // Determine group checkbox state
  const selectedSubs = subSensors.filter(s => {
    const sel = selectedSensors.get(s.port)
    return sel?.selected
  })
  const groupState: 'all' | 'none' | 'some' =
    selectedSubs.length === 0 ? 'none'
    : selectedSubs.length === subSensors.filter(s => s.hasData).length ? 'all'
    : 'some'

  const handleGroupToggle = () => {
    const shouldSelect = groupState !== 'all'
    for (const sensor of subSensors) {
      if (sensor.hasData) {
        onSelectionChange(sensor.port, shouldSelect)
      }
    }
  }

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      {/* Group header */}
      <div className={cn(
        'flex items-center gap-2 px-3 py-2',
        !hasAnyData ? 'bg-gray-50 opacity-60' : 'bg-white'
      )}>
        <label className={cn(
          'flex items-center gap-2 flex-1',
          !hasAnyData ? 'cursor-not-allowed' : 'cursor-pointer'
        )}>
          <input
            type="checkbox"
            checked={groupState === 'all'}
            ref={(el) => {
              if (el) el.indeterminate = groupState === 'some'
            }}
            disabled={!hasAnyData}
            onChange={handleGroupToggle}
            className={cn(
              'h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500',
              !hasAnyData && 'cursor-not-allowed'
            )}
          />
          <span className={cn(
            'inline-flex items-center justify-center',
            'w-7 h-5 text-xs font-bold rounded',
            !hasAnyData
              ? 'bg-gray-200 text-gray-400'
              : 'bg-gray-100 text-gray-700'
          )}>
            {group.parentPort}
          </span>
          <span className={cn(
            'text-sm truncate',
            !hasAnyData ? 'text-gray-400' : 'text-gray-900'
          )}>
            {group.productName}
          </span>
          {!hasAnyData && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-amber-700 bg-amber-100 rounded">
              <FontAwesomeIcon icon={faExclamationTriangle} className="h-3 w-3" />
              No data
            </span>
          )}
        </label>
      </div>

      {/* Sub-type rows */}
      {subSensors.map((sensor) => {
        const selection = selectedSensors.get(sensor.port)
        const isSelected = selection?.selected ?? false
        const currentUnitId = selection?.unitId ?? sensor.unitId
        const currentUnitSymbol = selection?.unitSymbol ?? sensor.unitSymbol
        const isDisabled = !sensor.hasData
        const units = availableUnits[sensor.unitCategoryId] || {}

        return (
          <div
            key={sensor.port}
            className={cn(
              'flex items-center justify-between gap-2 pl-10 pr-3 py-1.5',
              'border-t border-gray-50',
              isDisabled
                ? 'bg-gray-50 opacity-60'
                : isSelected ? 'bg-blue-50/30' : 'bg-white'
            )}
          >
            <label className={cn(
              'flex items-center gap-2 flex-1',
              isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
            )}>
              <input
                type="checkbox"
                checked={isSelected}
                disabled={isDisabled}
                onChange={(e) => onSelectionChange(sensor.port, e.target.checked)}
                className={cn(
                  'h-3.5 w-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500',
                  isDisabled && 'cursor-not-allowed'
                )}
              />
              <span className={cn(
                'text-xs font-medium',
                isDisabled ? 'text-gray-400' : 'text-gray-600'
              )}>
                {sensor.subTypeLabel || sensor.name}
              </span>
            </label>

            <UnitSelector
              sensor={sensor}
              isSelected={isSelected}
              isDisabled={isDisabled}
              currentUnitId={currentUnitId}
              currentUnitSymbol={currentUnitSymbol}
              units={units}
              onUnitChange={onUnitChange}
            />
          </div>
        )
      })}
    </div>
  )
}
